import { useForm, Control, FieldErrors, UseFormRegister, UseFormSetValue } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { UserData, useUpdateCurrentUser } from '@bizzabo/entities';
import { useCountryState } from '../helpers/states';
import { useCountryByCountryCode } from '../helpers/countries';
import validationSchema from '../validation/schema';

type SelectOptionType = {
    value: string;
    label: string;
};

export type EditFormValues = {
    firstName: string;
    lastName: string;
    title?: string;
    company?: string;
    web?: string;
    summary?: string;
    country?: SelectOptionType;
    state?: SelectOptionType;
    photoUrl?: string;
    linkedinId?: string;
    twitterHandle?: string;
};

type UseEditForm = (
    user: UserData,
    onEditSuccess: () => void
) => {
    register: UseFormRegister<EditFormValues>;
    control: Control<EditFormValues>;
    errors: FieldErrors<EditFormValues>;
    setValue: UseFormSetValue<EditFormValues>;
    onSubmit: () => void;
    isDirty: boolean;
};

const useDefaultValues = (user: UserData): EditFormValues => ({
    firstName: user.firstName ?? '',
    lastName: user.lastName ?? '',
    title: user.title ?? '',
    company: user.company ?? '',
    web: user.web ?? '',
    summary: user.summary ?? '',
    country: useCountryByCountryCode(user.country),
    state: useCountryState(user.country, user.state),
    photoUrl: user.photoUrl || '',
    linkedinId: user.linkedinId || '',
    twitterHandle: user.twitterHandle || '',
});

const useEditForm: UseEditForm = (user, onEditSuccess) => {
    const {
        handleSubmit,
        control,
        register,
        setValue,
        formState: { errors, isDirty },
    } = useForm<EditFormValues>({
        defaultValues: useDefaultValues(user),
        resolver: yupResolver(validationSchema),
        mode: 'onSubmit',
    });

    const updateCurrentUser = useUpdateCurrentUser();

    const onSubmit = handleSubmit(formState => {
        updateCurrentUser(
            {
                eventId: user.eventId,
                userProfileId: user.userProfileId,
                payload: {
                    ...formState,
                    country: formState.country?.value || '',
                    state: formState.state?.value || '',
                },
            },
            { onSuccess: onEditSuccess }
        );
    });

    return {
        register,
        control,
        onSubmit,
        errors,
        setValue,
        isDirty,
    };
};

export default useEditForm;
